import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Swal from "sweetalert2";
import axios from 'axios';
import * as pdfjsLib from "pdfjs-dist/webpack";

export default function EditBlog() {
    const { id } = useParams();
    const [loader, setLoader] = useState(false)
    const [blog, setBlog] = useState({});
    const [pdfText, setPdfText] = useState("");
    const [pdfPreview, setPdfPreview] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

    const navigate = useNavigate();
    const getBlogData = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/blog/${id}`);
            if (res.status === 200) {
                const data = res.data.data;
                setBlog(data);
                if (data.pdf) {
                    setPdfPreviewUrl(`${process.env.REACT_APP_BACKEND_BASE_URL}/uploads/blog/${data.pdf}`);
                }
                if (data.photo) {
                    setSelectedFile(`${process.env.REACT_APP_BACKEND_BASE_URL}/uploads/blog/${data.photo}`);
                }
                if (data.description) {
                    setPdfText(data.description);
                }
            } else {
                setBlog({});
            }
        } catch (err) {
            console.log(err);
            setLoader(true);
        }
    };
    useEffect(() => {
        if (id) getBlogData();
    }, [id]);

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        const file = files[0];
        if (file) {
            setSelectedFile(URL.createObjectURL(file));
            setBlog({
                ...blog,
                [name]: file
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setBlog({
            ...blog,
            [name]: value,
        });
    };

    const handleDescriptionChange = (e) => {
        const { value } = e.target;
        setBlog({ ...blog, description: value });
        setPdfText(value);
    };

    const extractTextFromPdf = async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        let textContent = "";

        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const text = await page.getTextContent();
            const pageText = text.items.map((item) => item.str).join(" ");
            textContent += pageText + "\n\n";
        }

        return textContent;
    };

    const handlePdfUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== "application/pdf") {
            Swal.fire("Please upload a valid PDF file!");
            return;
        }

        Swal.fire({
            title: "Extracting text...",
            text: "Please wait",
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            const extractedText = await extractTextFromPdf(file);

            // Split into paragraphs
            let paragraphs = extractedText
                .split(/\n\s*\n/) // split by blank lines
                .map(p => p.trim())
                .filter(p => p.length > 0);

            // Description = first 2 paragraphs
            const description = paragraphs.slice(0, 2).join("\n\n");
            setPdfText(description);

            // Save in state
            setBlog((prev) => ({
                ...prev,
                description,
                pdf: file, // store the PDF file
            }));

            // Show PDF preview (just filename)
            setPdfPreview(file.name);
            setPdfPreviewUrl(URL.createObjectURL(file));

            Swal.fire("Text extracted successfully!", "", "success");
        } catch (error) {
            console.error(error);
            Swal.fire("Failed to extract PDF content", "", "error");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!blog.title || !blog.description) {
            return Swal.fire({
                title: "Please fill all required fields",
                icon: "warning",
            });
        }
        setLoader(true);
        try {
            const formData = new FormData();

            Object.entries(blog).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== "") {
                    formData.append(key, value);
                }
            });

            const res = await axios.put(
                `${process.env.REACT_APP_BACKEND_BASE_URL}/blog/update/${id}`,
                formData
            );

            if (res.status === 200 && res.data.data) {
                Swal.fire({
                    icon: "success",
                    title: "Blog Updated Successfully",
                    timer: 1500,
                }).then(() => {
                    navigate("/bloglist");
                });
            } else {
                Swal.fire("Blog Updated Failed", "", "error");
            }
        } catch (err) {
            console.error(err);
            Swal.fire("Something went wrong!", err?.message, "error");
        } finally {
            setLoader(false);
        }
    };

    if (loader) {
        return (
            <div className="page-wrapper page-settings">
                <div className="error">
                    <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} />
                    <p style={{ marginTop: "15px", fontWeight: 500, color: "#555" }}>Loading, please wait...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="main-content">
            <div className="page-wrapper page-settings">
                <div className="content">
                    {/* Breadcrumb and Header Section with Card Shadow */}
                    <div
                        className="mb-4"
                        style={{
                            background: "#ffffff",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                            padding: "20px",
                        }}
                    >
                          <div className="content-page-header content-page-headersplit">
                            <h5>Edit Blog</h5>
                            <div className="list-btn">
                                <Link to="/bloglist" className="btn btn-cancel">
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Back
                                </Link>
                            </div>
                        </div>
                        <nav
                            aria-label="breadcrumb"
                            style={{ "--bs-breadcrumb-divider": "none", marginBottom: "15px" }}
                        >
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/dashboard">Dashboard</Link> /
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="/bloglist">Blog</Link> /
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    Edit Blog
                                </li>
                            </ol>
                        </nav>
                    
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="row g-4">
                            {/* LEFT SECTION - Blog Information */}
                            <div className="col-lg-6">
                                <div
                                    style={{
                                        background: "#ffffff",
                                        borderRadius: "8px",
                                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                                        padding: "20px",
                                    }}
                                >
                                    <h4
                                        style={{
                                            marginBottom: "20px",
                                            borderBottom: "2px solid #e6f6f5",
                                            paddingBottom: "12px",
                                            fontSize: "20px",
                                            fontWeight: 600,
                                            color: "#006e82",
                                        }}
                                    >
                                        Blog Information
                                    </h4>

                                    {/* Blog Title */}
                                    <div className="form-group mb-3">
                                        <label style={{ marginBottom: "8px", fontWeight: 600, color: "#0D233E" }}>
                                            Blog Title <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={blog.title || ""}
                                            className="form-control"
                                            onChange={handleInputChange}
                                            placeholder="Enter blog title"
                                            required
                                        />
                                    </div>

                                    {/* Description */}
                                    <div className="form-group mb-3">
                                        <label style={{ marginBottom: "8px", fontWeight: 600, color: "#0D233E" }}>
                                            Description <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <textarea
                                            name="description"
                                            rows="6"
                                            className="form-control"
                                            value={blog.description || pdfText || ""}
                                            onChange={handleDescriptionChange}
                                            placeholder="Enter blog description or upload PDF to extract text"
                                            required
                                        />
                                        {pdfText && (
                                            <small className="text-muted mt-2 d-block">
                                                <i className="fas fa-info-circle me-1"></i>
                                                Description extracted from PDF. You can edit it above.
                                            </small>
                                        )}
                                    </div>

                                    {/* PDF Upload */}
                                    <div className="form-group">
                                        <label style={{ marginBottom: "8px", fontWeight: 600, color: "#0D233E" }}>
                                            Upload PDF (Optional)
                                        </label>
                                        <input
                                            type="file"
                                            accept="application/pdf"
                                            className="form-control"
                                            onChange={handlePdfUpload}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT SECTION - Media Preview */}
                            <div className="col-lg-6">
                                <div
                                    style={{
                                        background: "#ffffff",
                                        borderRadius: "8px",
                                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                                        padding: "20px",
                                    }}
                                >
                                    <h4
                                        style={{
                                            marginBottom: "20px",
                                            borderBottom: "2px solid #e6f6f5",
                                            paddingBottom: "12px",
                                            fontSize: "20px",
                                            fontWeight: 600,
                                            color: "#006e82",
                                        }}
                                    >
                                        Media Preview
                                    </h4>

                                    {/* Image Upload */}
                                    <div className="form-group mb-3">
                                        <label style={{ marginBottom: "8px", fontWeight: 600, color: "#0D233E" }}>
                                            Blog Image
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="form-control"
                                            onChange={handleFileChange}
                                            name="photo"
                                        />
                                    </div>

                                    {/* Image Preview */}
                                    {selectedFile && (
                                        <div className="mb-3">
                                            <img
                                                src={selectedFile}
                                                alt="Preview"
                                                style={{
                                                    width: "100%",
                                                    height: "280px",
                                                    objectFit: "cover",
                                                    borderRadius: "8px",
                                                    border: "1px solid #E3E3E3",
                                                }}
                                            />
                                        </div>
                                    )}

                                    {/* PDF Preview */}
                                    {pdfPreviewUrl && (
                                        <div className="mt-3">
                                            {pdfPreview && (
                                                <div
                                                    style={{
                                                        background: "#f9fafb",
                                                        borderRadius: "8px",
                                                        padding: "15px",
                                                        marginBottom: "15px",
                                                    }}
                                                >
                                                    <p style={{ margin: 0, fontWeight: 600, color: "#006e82" }}>
                                                        <i className="fas fa-file-pdf me-2"></i>
                                                        PDF Preview: {pdfPreview}
                                                    </p>
                                                </div>
                                            )}
                                            <embed
                                                src={pdfPreviewUrl}
                                                type="application/pdf"
                                                width="100%"
                                                height="300px"
                                                style={{
                                                    borderRadius: "8px",
                                                    border: "1px solid #E3E3E3",
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ACTION BUTTONS */}
                            <div className="col-12">
                                <div className="btn-path">
                                    <button type="submit" className="btn btn-primary">
                                        <i className="fas fa-save me-2"></i>
                                        Update Blog
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}